import { DYNAMIC_ICONIC_SPRITE_URL } from '@dynamic-labs-sdk/client/core';

import type { EIP1193Provider } from '../../../EvmWalletProvider.types';
import type {
  EvmWalletDetectionResult,
  KnownEvmWalletFlags,
} from '../../windowInjected.types';

type ProviderWithFlags = EIP1193Provider & KnownEvmWalletFlags;

type DetectEvmWalletFromProviderParams = {
  provider: EIP1193Provider;
};

const getSpriteIcon = (name: string): string =>
  `${DYNAMIC_ICONIC_SPRITE_URL}#${name}`;

/**
 * Detects wallet identity from provider flags using hardcoded checks.
 *
 * This is essential for wallet app in-app browsers where the wallet injects
 * its provider with identifying flags (e.g., isMetaMask, isCoinbaseWallet).
 *
 * The order of checks matters - more specific flags should be checked first
 * to avoid false positives (e.g., some MetaMask forks also set isMetaMask).
 *
 * @param params.provider - The EIP-1193 provider to detect
 * @returns Detection result with displayName and icon, or null if unknown
  * @not-instrumented
 */
 
export const detectEvmWalletFromProvider = ({
  provider,
}: DetectEvmWalletFromProviderParams): EvmWalletDetectionResult | null => {
  const p = provider as ProviderWithFlags;

  // Check more specific flags first to avoid false positives
  // Many wallets set isMetaMask for compatibility, so check their unique flags first

  if (p.isCoinbaseWallet) {
    return {
      displayName: 'Coinbase Wallet',
      icon: getSpriteIcon('coinbasewallet'),
    };
  }

  if (p.isBraveWallet) {
    return {
      displayName: 'Brave Wallet',
      icon: getSpriteIcon('brave'),
    };
  }

  if (p.isPhantom) {
    return {
      displayName: 'Phantom',
      icon: getSpriteIcon('phantom'),
    };
  }

  if (p.isRabby) {
    return {
      displayName: 'Rabby Wallet',
      icon: getSpriteIcon('rabby'),
    };
  }

  if (p.isTrust) {
    return {
      displayName: 'Trust Wallet',
      icon: getSpriteIcon('trust'),
    };
  }

  if (p.isFrame) {
    return {
      displayName: 'Frame',
      icon: getSpriteIcon('frame'),
    };
  }

  if (p.isOkxWallet) {
    return {
      displayName: 'OKX Wallet',
      icon: getSpriteIcon('okx'),
    };
  }

  if (p.isZerion) {
    return {
      displayName: 'Zerion',
      icon: getSpriteIcon('zerion'),
    };
  }

  // MetaMask check comes last because many wallets set isMetaMask for compatibility
  if (p.isMetaMask) {
    return {
      displayName: 'MetaMask',
      icon: getSpriteIcon('metamask'),
    };
  }

  // Unknown wallet - don't register
  return null;
};
